import { Canvas } from '@react-three/fiber'
import { RelativeHeightSpin } from 'components/RelativeHeightSpin'
import { sceneDispatch } from 'features/assetViz/scenes/sceneDispatch'
import React, { useCallback, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { InView } from 'react-intersection-observer'
import styled from 'styled-components'
import { Vector2 } from 'three'
import {
  GeckoBinSize,
  GeckoComponentCoords,
  GeckoComponentModelType,
  GeckoComponentRect,
  GeckoComponentShape,
  GeckoFeature,
  GeckoFixedSensor,
  GeckoLegacyBoilerWalls,
  GeckoManway,
  GeckoMarkupLabel,
  GeckoMarkupLayer,
  GeckoMarkupPrimitive,
  GeckoMarkupRect,
  GeckoNozzle,
  GeckoPlate,
  GeckoRectangularManway,
  GeckoShellCourse,
  GeckoWeld,
} from 'types'
import { v4 as uuidv4 } from 'uuid'

import {
  AssetVizContextType,
  AssetVizInternalProvider,
} from './AssetVizInternalProvider'
import {
  AssetVizCompartmentsDisplayState,
  AssetVizController,
  AssetVizHeatmapDisplayState,
  AssetVizMode,
  AssetVizProps,
  AssetVizSceneConfigOptions,
} from './defines'
import { ErrorView } from './ErrorView'
import { useAssetVizImageHandler } from './imageGen/useAssetVizImageHandler'
import { defaultSceneFactories } from './scenes/defaultSceneFactories'
import { ClipConfig } from './systems/clipConfig'
import { DebugStats } from './systems/DebugStats'
import { ScreenshotHandler } from './systems/ScreenshotHandler'
import { VizTooltip } from './tooltip/VizTooltip'
import { HtmlOverlayEntry, MouseState, snapToBins, Vec3 } from './utils/helpers'
import { useExtractColors } from './utils/useExtractColors'

type AssetVizContainerProps = {
  cursor: string
  $textColor: string
}

const AssetVizContainer = styled.div<AssetVizContainerProps>`
  position: relative;
  width: 100%;
  height: 100%;
  margin: auto;
  cursor: ${(p) => p.cursor};
  overflow: hidden;

  color: ${(p) => p.$textColor};
`

const InViewStyle: React.CSSProperties = {
  position: 'absolute',
  width: '100%',
  height: '100%',
  overflow: 'clip',
}

const HtmlOverlaysContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  pointer-events: none;
`

const FakeScreenshotBackground = styled.div<{ $backgroundColor: string }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: ${({ $backgroundColor }) => $backgroundColor};
`

/**
 * The AssetViz component is fairly straightforward- it wraps a simple div
 * which is used by the AssetVizProvider to determine what to render where.
 * @param props     Props determining how the unit should be rendered, etc.
 */
export const AssetViz = React.forwardRef<
  AssetVizController,
  AssetVizProps & {
    /** An optional class name, commonly used for things like styled formatters */
    className?: string | undefined

    /** Optional child formatters to render on top of the viz */
    children?: React.ReactNode
  }
>((props, controllerRef) => {
  //----------------------------------------------------------------------------

  const containerRef = React.useRef<HTMLDivElement>(null!)

  const {
    assetModel,
    focusedCDF,
    focusedComponentID,
    componentModel,
    markupLayers,
    disable3D,
    inspection,
    pointCloud,
  } = React.useMemo(() => {
    const assetModel = props.assetModel
    const inspection = props.selectedInspection
    const pointCloud = props.selectedPointCloud
    const focusedCDF = props.componentDataFrames?.at(0)
    const focusedComponentID =
      focusedCDF?.tags.component ?? props.components?.at(0)?.id ?? ''
    const componentModel = assetModel?.data.components.get(focusedComponentID)
    const markupLayers = props.markupLayers ?? []
    const component = props.components?.find(
      (component) => component.id === componentModel?.id
    )
    const disable3D = component?.data.disable_3d ?? false
    return {
      assetModel,
      focusedCDF,
      focusedComponentID,
      componentModel,
      markupLayers,
      disable3D,
      inspection,
      pointCloud,
    }
  }, [
    props.assetModel,
    props.componentDataFrames,
    props.components,
    props.markupLayers,
    props.selectedInspection,
    props.selectedPointCloud,
  ])

  //----------------------------------------------------------------------------

  const { sceneConfig, onSceneConfigOptionsChanged } = props
  const [sceneConfigOptions, setSceneConfigOptions] =
    React.useState<AssetVizSceneConfigOptions>()
  React.useEffect(() => {
    if (!!onSceneConfigOptionsChanged && !!sceneConfigOptions)
      onSceneConfigOptionsChanged(sceneConfigOptions)
  }, [onSceneConfigOptionsChanged, sceneConfigOptions])

  React.useEffect(() => {
    const isEditingMarkup =
      (props.selection instanceof GeckoMarkupLayer ||
        props.selection instanceof GeckoMarkupPrimitive) &&
      !AssetVizMode.SelectMarkupRect
    const componentModelType = componentModel?.type
    setSceneConfigOptions({
      threeDimensionsEnabled:
        !isEditingMarkup &&
        !!assetModel &&
        (componentModelType === GeckoComponentModelType.VerticalTankShell ||
          componentModelType === GeckoComponentModelType.Cone ||
          componentModelType === GeckoComponentModelType.HorizontalTankShell ||
          componentModelType === GeckoComponentModelType.PipeSection ||
          componentModelType === GeckoComponentModelType.LaunchTube ||
          componentModelType === GeckoComponentModelType.DomeWall ||
          componentModelType === GeckoComponentModelType.EllipsoidalHead ||
          componentModelType ===
            GeckoComponentModelType.VerticalTankFoundation ||
          componentModelType === GeckoComponentModelType.PipeElbow ||
          componentModelType === GeckoComponentModelType.PipeTJoint ||
          componentModelType === GeckoComponentModelType.PipeEccentricReducer ||
          componentModelType === GeckoComponentModelType.Dome ||
          componentModelType === GeckoComponentModelType.PressureVesselShell ||
          componentModelType === GeckoComponentModelType.HemisphericalHead ||
          componentModelType === GeckoComponentModelType.HoodStraight ||
          componentModelType === GeckoComponentModelType.HoodBend ||
          !!props.showMultiComponentView) &&
        !disable3D,
      featuresEnabled: !!assetModel,
    })
  }, [
    assetModel,
    disable3D,
    props.selection,
    componentModel,
    props.showMultiComponentView,
  ])

  //----------------------------------------------------------------------------

  const mode = props.mode ?? AssetVizMode.NoInteraction
  const [userIsNavigating, setUserIsNavigating] = React.useState(false)
  const [userIsMousingOver, setUserIsMousingOver] = React.useState(false)
  const viewportSizeDuringAnimation = React.useRef<Vec3>([0, 0, 0] as Vec3)
  const [userClicks, setUserClicks] = React.useState<GeckoComponentCoords[]>([])
  const [draftSelection, setDraftSelection] = React.useState<
    GeckoComponentShape | GeckoMarkupPrimitive | GeckoFeature
  >()

  const colors = useExtractColors(
    props,
    !!(mode === AssetVizMode.Screenshot && sceneConfig?.invertColors)
  )
  React.useEffect(() => {
    if (
      sceneConfig &&
      containerRef.current &&
      sceneConfig.overrideCameraAspectRatio
    ) {
      sceneConfig.overrideCameraAspectRatio.current = -1
      containerRef.current.style.width = ''
      containerRef.current.style.height = ''
      if (mode === AssetVizMode.Screenshot) {
        if (sceneConfig.stretchAssetToFitScreen) {
          const bounds2D =
            componentModel instanceof GeckoLegacyBoilerWalls
              ? new Vector2(
                  componentModel.componentBounds.width.convertFromTubes(
                    componentModel.tubeSpacing!
                  ).num,
                  componentModel.componentBounds.height.num
                )
              : componentModel
                ? new Vector2(
                    componentModel.componentBounds.width.num,
                    componentModel.componentBounds.height.num
                  )
                : focusedCDF
                  ? new Vector2(
                      focusedCDF.data.geometry.width.num,
                      focusedCDF.data.geometry.height.num
                    )
                  : new Vector2(1, 1)

          sceneConfig.overrideCameraAspectRatio!.current =
            bounds2D.x / bounds2D.y
        }

        const outputAspect = 7 / 11
        if (sceneConfig.force11x7AspectRatio) {
          const canvasAspect =
            containerRef.current.clientHeight / containerRef.current.clientWidth
          if (canvasAspect > outputAspect)
            containerRef.current.style.height =
              containerRef.current.clientWidth * outputAspect + 'px'
          else
            containerRef.current.style.width =
              containerRef.current.clientHeight / outputAspect + 'px'
        }
      }
    }
  }, [componentModel, focusedCDF, mode, sceneConfig])

  React.useEffect(() => {
    if (props.onDraftSelected) {
      props.onDraftSelected(draftSelection)
    }
  }, [draftSelection, props])

  const onClicked = React.useCallback(
    (event: MouseState | undefined) => {
      const bounds = event?.componentBounds
      const primitive = event?.markupPrimitive

      const selectedMarkupID =
        props.selection instanceof GeckoMarkupLayer
          ? props.selection.id
          : props.selection instanceof GeckoMarkupPrimitive
            ? props.selection.markupLayerID
            : undefined

      const selectedMarkupGeometry =
        props.selection instanceof GeckoMarkupLayer
          ? props.selection.data.geometry
          : props.selection instanceof GeckoMarkupPrimitive
            ? props.selection.bounds.space
            : undefined

      // If there's no callback, do nothing
      if (!props.onSelected) {
        setUserClicks([])
        setDraftSelection(undefined)
      }
      // If a markup primitive was selected, plumb that back
      else if (primitive) {
        props.onSelected(primitive)
        setUserClicks([])
        setDraftSelection(undefined)
      } else if (event?.extrudingFeature) {
        props.onSelected(event.extrudingFeature)
        setDraftSelection(undefined)
      } else if (event?.plate) {
        props.onSelected(event.plate)
        setDraftSelection(undefined)
      }
      // If no component was selected, reflect that in the callback
      else if (!event || !bounds) {
        if (
          mode !== AssetVizMode.CreateMarkupLabel &&
          mode !== AssetVizMode.CreateMarkupRect &&
          mode !== AssetVizMode.CreateMarkupIcon
        ) {
          props.onSelected(undefined)
        }
        setUserClicks([])
        setDraftSelection(undefined)
      }
      // In bin selection mode, here's no need to use userClicks- just return
      // a new selection each time a user clicks
      else if (mode === AssetVizMode.SelectBin) {
        props.onSelected(draftSelection)
        setUserClicks([])
        setDraftSelection(undefined)
      }
      // Point selection (similar to bin selection) doesn't require userClicks-
      // just return the selected point each time the user clicks
      else if (
        mode === AssetVizMode.SelectPoint ||
        mode === AssetVizMode.CreateMarkupLabel ||
        mode === AssetVizMode.CreateMarkupIcon
      ) {
        if (
          draftSelection instanceof GeckoComponentCoords &&
          mode === AssetVizMode.CreateMarkupLabel &&
          !!selectedMarkupID &&
          selectedMarkupGeometry
        ) {
          const draftSelectionInMarkupUnits = draftSelection.project(
            selectedMarkupGeometry
          )

          const newMarkupPrimitive = GeckoMarkupLabel.CreateFromJSON(
            uuidv4(),
            selectedMarkupID,
            {
              kind: 'label',
              x: draftSelectionInMarkupUnits.x.num,
              y: draftSelectionInMarkupUnits.y.num,
              text: '',
            },
            selectedMarkupGeometry
          )
          props
            .onWrite?.(newMarkupPrimitive)
            .then(() => props.onSelected?.(newMarkupPrimitive))
        } else {
          props.onSelected(draftSelection)
        }
        setUserClicks([])
        setDraftSelection(undefined)
      }
      // If in rect selection mode, reset the user clicks every other click,
      // and return a new selection each time we create a new rect
      else if (
        mode === AssetVizMode.SelectRect ||
        mode === AssetVizMode.CreateMarkupRect
      ) {
        setUserClicks((prev) => {
          if (prev.length > 0) {
            return []
          }

          const coords = GeckoComponentCoords.CreateFromValues(
            bounds.componentID,
            event.componentX,
            bounds.x.unit,
            event.componentY,
            bounds.y.unit
          )
          return [coords]
        })
        if (draftSelection instanceof GeckoComponentRect) {
          if (
            mode === AssetVizMode.CreateMarkupRect &&
            !!selectedMarkupID &&
            selectedMarkupGeometry
          ) {
            const draftSelectionInMarkupUnits = draftSelection.project(
              selectedMarkupGeometry
            )

            const newMarkupPrimitive = GeckoMarkupRect.CreateFromJSON(
              uuidv4(),
              selectedMarkupID,
              {
                kind: 'rect',
                x_start: draftSelectionInMarkupUnits.x.start.num,
                y_start: draftSelectionInMarkupUnits.y.start.num,
                x_end: draftSelectionInMarkupUnits.x.end.num,
                y_end: draftSelectionInMarkupUnits.y.end.num,
              },
              selectedMarkupGeometry
            )
            props.onWrite?.(newMarkupPrimitive)
          } else {
            props.onSelected(draftSelection)
          }
        }
        setDraftSelection(undefined)
      }
    },
    [props, mode, draftSelection]
  )

  //----------------------------------------------------------------------------

  const [tooltipFrozen, setTooltipFrozen] = React.useState(false)
  React.useEffect(() => {
    const tooltipKeyListener = (event: KeyboardEvent) => {
      if (event.key === 'f')
        setTooltipFrozen((prev) => {
          // prevent user from freezing tooltip if not mousing over, instead they should disable it
          return !userIsMousingOver && !prev ? prev : !prev
        })
    }
    document.addEventListener('keypress', tooltipKeyListener)
    return () => {
      document.removeEventListener('keypress', tooltipKeyListener)
    }
  }, [userIsMousingOver])

  const onHovered = React.useCallback(
    (event: MouseState | undefined) => {
      // If a markup primitive was hovered...
      const markupPrimitive = event?.markupPrimitive
      if (markupPrimitive) {
        setDraftSelection(markupPrimitive)
        props.onHovered?.(tooltipFrozen, undefined)
        return
      } else if (event?.extrudingFeature) {
        setDraftSelection(event.extrudingFeature)
        props.onHovered?.(tooltipFrozen, undefined)
        return
      } else if (event?.plate) {
        setDraftSelection(event.plate)
        props.onHovered?.(tooltipFrozen, undefined)
        return
      }

      if (!event || !event.componentBounds || userIsNavigating) {
        setDraftSelection(undefined)
        props.onHovered?.(tooltipFrozen, undefined)
        setUserIsMousingOver(false)
        return
      }
      setUserIsMousingOver(true)
      const binSizeToUse =
        event.componentDataFrame?.data.binning.bin_size ??
        GeckoBinSize.OneInchBins

      const dataBoundsToUse =
        event.componentDataFrame?.data.geometry ??
        GeckoComponentRect.CreateFromXYs(
          event.componentBounds.componentID,
          event.componentBounds.start.x
            .minus(binSizeToUse.width.scale(0.5))
            .toNumber(event.componentBounds.x.unit),
          event.componentBounds.start.y
            .minus(binSizeToUse.height.scale(0.5))
            .toNumber(event.componentBounds.y.unit),
          event.componentBounds.end.x
            .plus(binSizeToUse.width.scale(0.5))
            .toNumber(event.componentBounds.x.unit),
          event.componentBounds.end.y
            .plus(binSizeToUse.height.scale(0.5))
            .toNumber(event.componentBounds.y.unit),
          event.componentBounds.x.unit,
          event.componentBounds.y.unit
        )

      const bin = snapToBins(
        event.componentX,
        event.componentY,
        event.componentX,
        event.componentY,
        event.componentWrapsX,
        event.componentWrapsY,
        event.componentBounds,
        binSizeToUse,
        dataBoundsToUse
      )

      // TODO ARCH-206: clean this fix up
      const halfBinWidth = binSizeToUse.width.toNumber(bin.end.x.unit) / 2
      const halfBinHeight = binSizeToUse.height.toNumber(bin.end.y.unit) / 2

      const binCenterX = bin.x.end.num - halfBinWidth
      const binCenterY = bin.y.end.num - halfBinHeight

      const adjustedBinCenterX = event.componentWrapsX
        ? wrapWithinBounds(
            binCenterX,
            event.componentBounds.x.delta.num,
            event.componentBounds.x.start.num
          )
        : binCenterX
      const adjustedBinCenterY = event.componentWrapsY
        ? wrapWithinBounds(
            binCenterY,
            event.componentBounds.y.delta.num,
            event.componentBounds.y.start.num
          )
        : binCenterY
      const coordsToShow: GeckoComponentCoords | undefined =
        GeckoComponentCoords.CreateFromValues(
          bin.componentID,
          adjustedBinCenterX,
          bin.x.unit,
          adjustedBinCenterY,
          bin.y.unit
        )

      GeckoComponentCoords.CreateFromMeasurements(
        bin.componentID,
        bin.end.x.minus(binSizeToUse.width.scale(0.5)),
        bin.end.y.minus(binSizeToUse.height.scale(0.5))
      )
      props.onHovered?.(tooltipFrozen, {
        mouseX: event.mouseX,
        mouseY: event.mouseY,
        positionX: event.positionX,
        positionY: event.positionY,
        positionZ: event.positionZ,
        coords: coordsToShow,
        componentDataFrame: event.componentDataFrame,
        componentModel: event.componentModel,
        shellCourse: event.course,
        waterwall: event.wall,
        markupPrimitive,
      })

      // If the user's selecting a point...
      if (
        mode === AssetVizMode.SelectPoint ||
        mode === AssetVizMode.CreateMarkupLabel ||
        mode === AssetVizMode.CreateMarkupIcon
      ) {
        setDraftSelection(coordsToShow)
      }
      // If the user's selecting a rect...
      else if (
        mode === AssetVizMode.SelectRect ||
        mode === AssetVizMode.CreateMarkupRect
      ) {
        // If the user's clicked once, draft a selection of just one point
        const firstClick = userClicks[0]
        if (!firstClick) {
          setDraftSelection(undefined)
        }
        // If the user's clicked twice, complete the draft selection with multiple points
        else if (firstClick) {
          const rect = snapToBins(
            firstClick.x.num,
            firstClick.y.num,
            event.componentX,
            event.componentY,
            event.componentWrapsX,
            event.componentWrapsY,
            event.componentBounds,
            binSizeToUse,
            dataBoundsToUse
          )
          setDraftSelection(rect)
        }
      }
      // If the user's selecting markup rects...
      else if (mode === AssetVizMode.SelectMarkupRect) {
        if (markupPrimitive) {
          setDraftSelection(markupPrimitive)
        } else {
          setDraftSelection(undefined)
        }
      }
      // Else, the user's selecting bins...
      else {
        setDraftSelection(bin)
      }
    },
    [userIsNavigating, props, mode, userClicks, tooltipFrozen]
  )

  React.useEffect(() => {
    // Reset the draft selection if the user changes selection modes
    setUserClicks([])
    setDraftSelection(undefined)
  }, [mode])

  React.useEffect(() => {
    const onEscapePressed = (event: KeyboardEvent) => {
      // Reset the draft selection if the user presses "escape"
      if (event.key === 'Escape' && userClicks.length > 0) {
        setUserClicks([])
        setDraftSelection(undefined)
        event.preventDefault()
      }
    }
    document.addEventListener('keydown', onEscapePressed)
    return () => document.removeEventListener('keydown', onEscapePressed)
  }, [userClicks.length])

  //----------------------------------------------------------------------------

  const [inView, setInView] = React.useState(false)
  const cursor = React.useMemo<string>(() => {
    if (mode === AssetVizMode.NoInteraction) return 'auto'
    if (!userIsMousingOver) return 'auto'
    if (userIsNavigating) return 'grabbing'
    if (mode === AssetVizMode.SelectBin) return 'pointer'
    if (
      draftSelection instanceof GeckoShellCourse ||
      draftSelection instanceof GeckoManway ||
      draftSelection instanceof GeckoRectangularManway ||
      draftSelection instanceof GeckoNozzle ||
      draftSelection instanceof GeckoPlate ||
      draftSelection instanceof GeckoFixedSensor ||
      draftSelection instanceof GeckoWeld ||
      draftSelection instanceof GeckoMarkupPrimitive
    )
      return 'pointer'
    if (
      mode === AssetVizMode.CreateMarkupRect ||
      mode === AssetVizMode.CreateMarkupLabel ||
      mode === AssetVizMode.CreateMarkupIcon
    )
      return 'crosshair'
    return 'auto'
  }, [draftSelection, mode, userIsMousingOver, userIsNavigating])

  // HtmlOverlays must be handled in a custom way, using a combination of refs
  // for easy access and an actual array for iteration
  const htmlOverlaysRef = React.useRef(new Array<HtmlOverlayEntry>())
  const [htmlOverlays, setHtmlOverlays] = React.useState<HtmlOverlayEntry[]>([])
  React.useEffect(() => {
    htmlOverlaysRef.current = htmlOverlays
  }, [htmlOverlays])

  // Save (and pass up via ref) a "controller" object which allows users to
  // call methods like zoomToFit() or cancelZoom()
  const [controller, setController] = React.useState<AssetVizController>({
    zoomToFit: () => new Promise((_, reject) => reject()),
    snapToFit: () => new Promise((_, reject) => reject()),
    centerOn: () => new Promise((_, reject) => reject()),
    cancelZoom: () => {},
    takeScreenshot: () => new Promise(() => {}),
    resetCamera: () => {},
  })
  React.useImperativeHandle(controllerRef, () => controller, [controller])

  const [showDataLoadingDisplay, setShowDataLoadingDisplay] =
    React.useState(false)
  const [textureLoading, setTextureLoading] = useState(true)

  useAssetVizImageHandler(textureLoading, containerRef, props.setAssetImage)

  const scene = React.useMemo(
    () =>
      sceneDispatch(
        [...(props.sceneFactoryConfigs ?? []), ...defaultSceneFactories],
        {
          showMultiComponentView: props.showMultiComponentView ?? false,
          showAxesHelper: props.showAxesHelper ?? false,
          assetModel: assetModel,
          componentModel: componentModel,
          cdf: focusedCDF,
          inspection: inspection,
          pointCloud: pointCloud,
        }
      ),
    [
      assetModel,
      componentModel,
      focusedCDF,
      inspection,
      pointCloud,
      props.showMultiComponentView,
      props.showAxesHelper,
      props.sceneFactoryConfigs,
    ]
  )

  //----------------------------------------------------------------------------
  const { getColorThresholdForComponent } = props
  const asyncGetColorThresholdForCdf = useCallback<
    AssetVizContextType['getColorThresholdForComponent']
  >(
    async (args) => await getColorThresholdForComponent?.(args),
    [getColorThresholdForComponent]
  )

  //----------------------------------------------------------------------------

  return (
    <AssetVizContainer
      ref={containerRef}
      className={props.className}
      $textColor={colors.featureLabelColorString}
      cursor={cursor}>
      <RelativeHeightSpin spinning={showDataLoadingDisplay}>
        <FakeScreenshotBackground
          data-html2canvas-ignore
          $backgroundColor={colors.backgroundColorString}
        />
        <ErrorBoundary FallbackComponent={ErrorView} fallback={undefined}>
          <InView as='div' onChange={setInView} style={InViewStyle}>
            {!inView && !props.offscreenRender ? (
              props.loadingView
            ) : (
              <AssetVizInternalProvider
                value={{
                  assetVizDom: containerRef,
                  // Backing data ------------------------------------------------
                  assetModel,
                  focusedComponentID,
                  componentDataFrames: props.componentDataFrames ?? [],
                  focusedCDF,
                  getColorThresholdForComponent: asyncGetColorThresholdForCdf,
                  markupLayers,
                  markupLayerLabelVisibility:
                    props.markupLayerLabelVisibility ?? [],
                  // Display controls --------------------------------------------
                  show3D:
                    ((sceneConfig?.show3D ?? true) &&
                      sceneConfigOptions?.threeDimensionsEnabled) ??
                    false,
                  showRulers: sceneConfig?.showRulers ?? false,
                  heatmapDisplayState:
                    sceneConfig?.heatmapDisplayState ??
                    AssetVizHeatmapDisplayState.Sharp,
                  showFeatures: sceneConfig?.showFeatures ?? false,
                  showCompass: sceneConfig?.showCompass ?? false,
                  showFeatureLabels: sceneConfig?.showFeatureLabels ?? false,
                  compartmentsDisplayState:
                    sceneConfig?.compartmentsDisplayState ??
                    AssetVizCompartmentsDisplayState.NominalPlates,
                  showStructures: sceneConfig?.showStructures ?? true,
                  showHtmlOverlayDebugColors:
                    sceneConfig?.showHtmlOverlayDebugColors ?? false,
                  showDebugStats: sceneConfig?.showDebugStats ?? false,
                  showFeatureDebugColors:
                    sceneConfig?.showFeatureDebugColors ?? false,
                  showShellCourseDebugColors:
                    sceneConfig?.showShellCourseDebugColors ?? false,
                  showPlateFeatureDebugColors:
                    sceneConfig?.showPlateFeatureDebugColors ?? false,
                  showBinValues: sceneConfig?.showBinValues ?? false,
                  showLegend: sceneConfig?.showLegend ?? true,
                  overrideCameraAspectRatio:
                    sceneConfig?.overrideCameraAspectRatio,
                  force11x7AspectRatio:
                    sceneConfig?.force11x7AspectRatio ?? false,
                  stretchAssetToFitScreen:
                    sceneConfig?.stretchAssetToFitScreen ?? false,
                  invertColors: sceneConfig?.invertColors ?? true,
                  clipConfig: sceneConfig?.clipConfig ?? new ClipConfig(),
                  activePhotogrammetryModel:
                    sceneConfig?.activePhotogrammetryModel,
                  activePhotogrammetryTexture:
                    sceneConfig?.activePhotogrammetryTexture,
                  setPhotogrammetryTextureLoading:
                    sceneConfig?.setPhotogrammetryTextureLoading,
                  showPhotogrammetryTexture:
                    sceneConfig?.showPhotogrammetryTexture ?? false,
                  hasPhotogrammetryTextureUpdates:
                    sceneConfig?.hasPhotogrammetryTextureUpdates ?? false,
                  heatmapOpacity: sceneConfig?.heatmapOpacity ?? 1.0,
                  assetOpacity: sceneConfig?.assetOpacity ?? 1.0,
                  emissiveColor: sceneConfig?.emissiveColor,
                  ...colors,
                  setShowDataLoadingDisplay,
                  cameraFitMultiplier: props.cameraFitMultiplier,
                  // View controls -----------------------------------------------
                  mode,
                  showMultiComponentViewer:
                    props.showMultiComponentView ?? false,
                  userIsNavigating,
                  setUserIsNavigating,
                  offscreenRender: props.offscreenRender,
                  viewportSizeDuringAnimation,
                  draftSelection: draftSelection,
                  selection: props.selection,
                  cdfToFilteredValueRange:
                    props.cdfToFilteredValueRange ?? (() => undefined),
                  cdfToSelectedCategoricalValues:
                    props.cdfToSelectedCategoricalValues ?? (() => undefined),
                  onClicked,
                  onHovered,
                  onWrite: props.onWrite ?? (() => Promise.resolve()),
                  refreshAndSelectLayer:
                    props.refreshAndSelectLayer ?? (() => Promise.resolve()),
                  deletePrimitive:
                    props.deletePrimitive ?? (() => Promise.resolve()),
                  // Internal properties -----------------------------------------
                  textureLoading,
                  setTextureLoading,
                  setController,
                  htmlOverlaysRef,
                  setHtmlOverlays,
                }}>
                <Canvas
                  id={props.id}
                  gl={{
                    antialias: true,
                    preserveDrawingBuffer: true, // Allows screenshots & Sentry recordings
                    localClippingEnabled: true,
                  }}
                  onCreated={(state) => {
                    // Counterintuitively, this is necessary for touchscreens to work properly
                    state.gl.domElement.style.touchAction = 'none'
                  }}>
                  <ScreenshotHandler />
                  {scene}
                </Canvas>
                <VizTooltip
                  isVisible={
                    !!props.vizToolTipContent &&
                    (tooltipFrozen || userIsMousingOver)
                  }
                  isFrozen={tooltipFrozen}>
                  {props.vizToolTipContent}
                </VizTooltip>
              </AssetVizInternalProvider>
            )}
          </InView>
          <HtmlOverlaysContainer>
            {htmlOverlays.map(({ id, node }) => (
              <div key={id}>{node}</div>
            ))}
          </HtmlOverlaysContainer>
          {sceneConfig?.showDebugStats ? <DebugStats /> : undefined}
          {props.children}
        </ErrorBoundary>
      </RelativeHeightSpin>
    </AssetVizContainer>
  )
})

/**
 *
 * @param value value to wrap
 * @param boundsLength delta between start and end of bounds to wrap within
 * @param boundsStart start of bounds to wrap within
 * @returns
 */
function wrapWithinBounds(
  value: number,
  boundsLength: number,
  boundsStart: number
) {
  return positiveModulo(value - boundsStart, boundsLength)
}

function positiveModulo(n: number, m: number) {
  return ((n % m) + m) % m
}

AssetViz.displayName = 'AssetViz'
